import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useToggle } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  BankingValueObjectsPaymentRequestItemStatusEnum,
  PaymentRequestDetailPageDocument,
  PaymentRequestItemFieldsFragment,
  PaymentRequestItemsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { PaymentsOptionItem } from 'modules/banking/components/AddPaymentRequestsToPaymentForm'
import { PayPaymentRequestsInBulkModal } from 'modules/banking/components/PayPaymentRequestsInBulkModal'
import { PaymentRequestItemTransfersSidePanel } from 'modules/banking/components/PaymentRequestItemTransfersSidePanel'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning' | 'critical'

const getItemStatusColor = (
  status: BankingValueObjectsPaymentRequestItemStatusEnum
): StatusColor => {
  const statusColorMap: Record<BankingValueObjectsPaymentRequestItemStatusEnum, StatusColor> = {
    init: 'neutral',
    in_payment: 'info',
    in_progress: 'info',
    paid: 'positive',
    rejected: 'critical',
  }
  return statusColorMap[status] ?? 'neutral'
}

const getItemStatusLabel = (status: BankingValueObjectsPaymentRequestItemStatusEnum): string => {
  const statusLabelMap: Record<BankingValueObjectsPaymentRequestItemStatusEnum, string> = {
    init: i18n.t('banking.paymentRequests.itemStatus.init'),
    in_payment: i18n.t('banking.paymentRequests.itemStatus.inPayment'),
    in_progress: i18n.t('banking.paymentRequests.itemStatus.inProgress'),
    paid: i18n.t('banking.paymentRequests.itemStatus.paid'),
    rejected: i18n.t('banking.paymentRequests.itemStatus.rejected'),
  }
  return statusLabelMap[status] ?? status
}

const PaymentRequestDetailPage = () => {
  const { paymentRequestId } = useParams<{ paymentRequestId: string }>()
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [selectedItem, setSelectedItem] = useState<PaymentRequestItemFieldsFragment | null>(null)
  const [sidePanelOpen, toggleSidePanel] = useToggle(false)
  const [sendToPayModalOpen, toggleSendToPayModal] = useToggle(false)

  const { data: pageData } = useQuery(PaymentRequestDetailPageDocument)

  const paymentsOptions: PaymentsOptionItem[] =
    pageData?.banking?.payments?.flatMap((payment) => {
      return { label: payment.label, value: payment.id }
    }) || []

  const goToPayments = () => navigate(navigation.finance.treasury.banking.payments.fullPath())

  const handleCloseSidePanel = () => {
    toggleSidePanel(false)()
    setSelectedItem(null)
  }

  const dataSource = useGraphqlDataCollectionSource({
    document: PaymentRequestItemsDataCollectionDocument,
    mapData: (data) => {
      return data.banking.paymentRequestItemsConnection.edges.map(
        (edge: { node: PaymentRequestItemFieldsFragment }) => edge.node
      )
    },
    mapVariables: () => ({
      bankingPaymentRequestIds: paymentRequestId ? [Number(paymentRequestId)] : undefined,
    }),
    paginationType: 'pages',
    perPage: 10,
    mapPagination: (data) => ({
      total: data.banking.paymentRequestItemsConnection.totalCount ?? 0,
      hasNextPage: data.banking.paymentRequestItemsConnection.pageInfo.hasNextPage ?? false,
      cursor: data.banking.paymentRequestItemsConnection.pageInfo.endCursor ?? undefined,
    }),
    itemOnClick: (item: PaymentRequestItemFieldsFragment) => () => {
      if (item.status !== 'init') {
        setSelectedItem(item)
        toggleSidePanel(true)()
      }
    },
  })

  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.paymentRequests.itemColumns.beneficiary'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'text' as const,
          value: row.beneficiaryName ?? '-',
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.itemColumns.amount'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({
            amount: row.amountCents,
            currency: row.paymentRequest?.currency ?? 'EUR',
            locale,
          }),
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.itemColumns.status'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'status' as const,
          value: {
            label: getItemStatusLabel(row.status),
            status: getItemStatusColor(row.status),
          },
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns]
  )

  const handleActionClick = (key: string) => {
    if (key === 'send_to_pay') {
      toggleSendToPayModal(true)()
    }
  }

  return (
    <ResponsivePage.Body variant="full" onActionClick={handleActionClick}>
      <OneDataCollection
        source={dataSource}
        visualizations={visualizations}
        emptyStates={{
          'no-data': {
            title: i18n.t('banking.paymentRequests.detail.emptyState.title'),
            description: i18n.t('banking.paymentRequests.detail.emptyState.description'),
          },
        }}
      />
      {sidePanelOpen && selectedItem && (
        <PaymentRequestItemTransfersSidePanel
          paymentRequestItemId={selectedItem.id}
          beneficiaryName={selectedItem.beneficiaryName ?? '-'}
          onClose={handleCloseSidePanel}
        />
      )}
      {sendToPayModalOpen && paymentRequestId && (
        <PayPaymentRequestsInBulkModal
          onSuccess={goToPayments}
          onClose={() => toggleSendToPayModal(false)()}
          paymentRequestIds={[Number(paymentRequestId)]}
          paymentsOptions={paymentsOptions}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default PaymentRequestDetailPage
